import React, { useState } from 'react';
import { View } from 'react-native';
import PropTypes from 'prop-types';
import Theme from 'Theme';
import { Background, Button, Promo } from 'LocalGluons';
import NavigationService from 'NavigationService';
import { t } from 'Locale';
import { withStore } from 'Store';
import TvGuide from 'LocalGluons/components/TvGuidePreview';
import { getAssetImageUrl, getItemStartTime, getProgressPercentage } from 'Utils';
import { isContentBlocked, isContentLocked } from 'Utils/parentalControlUtils';
import { withConfirmationModal } from 'LocalGluons/components/ConfirmationModal';
import { withNavigationProps } from '../../Navigation/navigationPropsContext'; //src/runtime/Navigation/navigationPropsContext
import { lockToLandscape } from 'DeviceUtils';
import { logEvent } from 'AppManager';
import defaultStyles from './WatchTV.jss';
import { useIsFocused } from '@react-navigation/native';

class WatchTV extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            promoData: null,
            promoDataLoaded: false,
            updatePromoData: false
        };

        this.navWillFocusListener = null;
    }

    componentDidMount() {
        const { navigation } = this.props;
        NavigationService.setAppNavigationInstance(navigation);
        this.navWillFocusListener = navigation.addListener('focus', () => {
            this.setState({
                updatePromoData: true
            });
        });
    }

    componentWillUnmount() {
        if (this.navWillFocusListener) {
            this.navWillFocusListener();
        }
    }

    onTvGuideButtonPress = () => {
        const { navigation } = this.props;
        navigation.navigate('TvGuide');
        logEvent({
            type: 'user-flow',
            subtype: 'watch-tv',
            content1: 'open-epg-button',
            content2: '',
            context: '/epg'
        });
    };

    promoItemsLoaded = (item) => {
        const { parentalLockConfig } = this.props;
        const { promoData, updatePromoData } = this.state;

        if (!promoData || updatePromoData) {
            if (item) {
                const ageBlock = isContentBlocked(item.data?.parentalRating, parentalLockConfig);
                const adultLock = isContentLocked(item.isAdult(), parentalLockConfig);
                const startTime = getItemStartTime(item.data);
                const subTitle =
                    item.data.channelname && startTime && `${item.data.channelname} | ${startTime}`;

                this.setState({
                    promoData: {
                        item,
                        subTitle,
                        isBlocked: ageBlock,
                        isAgeBlocked: ageBlock,
                        isAdultLocked: adultLock,
                        header: item.getCategory(),
                        title: adultLock ? t('COMMON.LOCKED_ADULT_CONTENT') : item.getTitle(),
                        progress: getProgressPercentage(item),
                        imageUrl: adultLock ? '' : getAssetImageUrl(item, 'PREVIEW')
                    },
                    promoDataLoaded: true,
                    updatePromoData: false
                });
            } else {
                this.setState({ promoDataLoaded: true });
            }
        }
    };

    render() {
        const {
            navigation,
            setPlayerData,
            setProtectedContent,
            userData,
            navigationPropsContext,
            setScrollY,
            scrollY
        } = this.props;
        const { promoData, promoDataLoaded, updatePromoData } = this.state;
        const { updateNavigationProps } = navigationPropsContext;

        return (
            <Background>
                <TvGuide
                    updatePromoData={updatePromoData}
                    onSelectChannel={(currentProgram) => {
                        /* TODO: ensure locking to landscape on iOS, see PRJ1260-2665.
                            This is partial fix, must be investigated deeper
                        *  It seems that player cannot lock itself to landscape when previous scene was native modal
                        *  UserData check is here because we dont want to lock immediately when user is not logged,
                        *  this can cause orientation problems in error dialogs thrown from player
                        * */
                        if (userData) lockToLandscape();
                        setPlayerData({
                            item: currentProgram
                        });
                    }}
                    onSelectProgram={(currentProgram) => {
                        updateNavigationProps({ item: currentProgram });
                        navigation.navigate('Details', {
                            id: currentProgram.data.id,
                            type: currentProgram.modelType,
                            platform: currentProgram.data?.pckg?.platform
                        });
                    }}
                    promoItemsLoaded={this.promoItemsLoaded}
                    style={defaultStyles.tvGuide}
                    setScrollY={setScrollY}
                    scrollY={scrollY}
                >
                    <View
                        testID="main_carousel"
                        style={{ margin: Theme.primaryMargin, marginBottom: 0 }}
                    >
                        <Promo
                            testID="watchTV_top_promo"
                            data={promoData}
                            style={defaultStyles.promo}
                            onItemPress={(item, data) => {
                                if (data.isBlocked) {
                                    setProtectedContent(item, () => {
                                        navigation.navigate('Details', {
                                            id: item.data.id,
                                            type: item.modelType
                                        });
                                    });
                                } else {
                                    navigation.navigate('Details', {
                                        id: item.data.id,
                                        type: item.modelType
                                    });
                                    updateNavigationProps({ item: item });
                                }
                            }}
                            loaded={promoDataLoaded}
                        />
                    </View>
                    <Button
                        style={{
                            button: defaultStyles.tvGuideButton
                        }}
                        onPress={this.onTvGuideButtonPress}
                        iconName="tv_guide"
                        title={t('TV.GUIDE')}
                        testID="EPG_button"
                    />
                </TvGuide>
            </Background>
        );
    }
}

WatchTV.defaultProps = {
    userData: null
};

WatchTV.propTypes = {
    navigation: PropTypes.shape({}).isRequired,
    setPlayerData: PropTypes.func.isRequired,
    setProtectedContent: PropTypes.func.isRequired,
    parentalLockConfig: PropTypes.shape({}).isRequired,
    userData: PropTypes.shape({})
};

const StoredWatchTV = withStore(WatchTV);
const WithModalStoredWatchTV = withConfirmationModal(StoredWatchTV);
const WithNavigationPropsWatchTV = withNavigationProps(WithModalStoredWatchTV);

const withScreenFocusOnly = (WrappedComponent) => {
    return (props) => {
        const isFocused = useIsFocused();
        const [scrollY, setScrollY] = useState(0);
        if (!isFocused) return null;

        return (
            <WrappedComponent
                {...props}
                isFocused={isFocused}
                setScrollY={setScrollY}
                scrollY={scrollY}
            />
        );
    };
};

export default withScreenFocusOnly(WithNavigationPropsWatchTV);
